import React, { useState, useEffect, useCallback } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  Linking,
  TouchableOpacity,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { useAuth } from "@components/auth/useAuth";
import { getRules } from "common247";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors, Spacing } from "@/constants/ThemeColours";
import {
  CrashlyticsService,
  type ICrashlyticsInfo,
} from "@/services/crashlytics-service";
import { type ICrashTombstone } from "@/services/crash-reporter";
import { flushQueue } from "@/services/batch-logger";

/**
 * Crashlytics Admin Screen.
 * Provides administrators with visibility into Firebase Crashlytics configuration,
 * remote logging pipeline health, crash tombstone inspection, and simulated test crashes.
 *
 * @returns {React.JSX.Element} The rendered admin screen.
 */
export default function CrashlyticsAdminScreen(): React.JSX.Element {
  const { user } = useAuth(true);

  const [info, setInfo] = useState<ICrashlyticsInfo>(CrashlyticsService.getInfo());
  const [tombstones, setTombstones] = useState<ICrashTombstone[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [toggling, setToggling] = useState<boolean>(false);
  const [testingCrash, setTestingCrash] = useState<boolean>(false);
  const [expandedTombstoneIndex, setExpandedTombstoneIndex] = useState<number | null>(null);

  const textMuted = useThemeColor({}, "textMuted");
  const borderColor = useThemeColor({}, "border");
  const cardBg = useThemeColor({}, "card");

  const rules = getRules();
  const isAdmin = user ? rules.isAdmin.can(user.claims, undefined) : false;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const currentInfo = CrashlyticsService.getInfo();
      setInfo(currentInfo);
      const records = await CrashlyticsService.getRecentTombstones();
      setTombstones(records);
    } catch (err: any) {
      console.error("[CrashlyticsAdmin] Failed to load data:", err);
      CustomAlert.alert("Error", err?.message || "Failed to load Crashlytics diagnostic data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!user || !isAdmin) {
    return (
      <ThemedScreen title="Crashlytics">
        <View style={styles.unauthorizedContainer}>
          <ThemedText type="subtitle" style={{ color: "#E74C3C" }}>
            Access Denied
          </ThemedText>
          <ThemedText style={{ color: textMuted, marginTop: 8 }}>
            This diagnostic console is restricted strictly to platform administrators.
          </ThemedText>
        </View>
      </ThemedScreen>
    );
  }

  const handleToggleCollection = async () => {
    const nextState = !info.collectionEnabled;
    setToggling(true);
    try {
      await CrashlyticsService.setCollectionEnabled(nextState);
      setInfo(CrashlyticsService.getInfo());
      CustomAlert.alert(
        "Crash Collection Updated",
        `Crashlytics error collection is now ${nextState ? "ENABLED" : "DISABLED"}.`
      );
    } catch (err: any) {
      CustomAlert.alert("Error", err?.message || "Failed to update crash collection setting.");
    } finally {
      setToggling(false);
    }
  };

  const handleOpenConsole = async () => {
    try {
      const supported = await Linking.canOpenURL(info.consoleUrl);
      if (supported) {
        await Linking.openURL(info.consoleUrl);
      } else {
        CustomAlert.alert("Cannot Open URL", `Please navigate directly to: ${info.consoleUrl}`);
      }
    } catch (err: any) {
      CustomAlert.alert("Error", err?.message || "Failed to open Firebase console.");
    }
  };

  const handleTriggerTestCrash = () => {
    CustomAlert.alert(
      "Simulate Crash",
      "This will trigger a simulated test exception, record a crash tombstone, and immediately flush the report to the remote logging pipeline. Proceed?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Trigger Test Crash",
          style: "destructive",
          onPress: async () => {
            setTestingCrash(true);
            try {
              const res = await CrashlyticsService.testCrash();
              await loadData();
              CustomAlert.alert("Success", res.message);
            } catch (err: any) {
              CustomAlert.alert("Error", err?.message || "Failed to record simulated crash.");
            } finally {
              setTestingCrash(false);
            }
          },
        },
      ]
    );
  };

  const handleFlushLogs = async () => {
    try {
      await flushQueue();
      CustomAlert.alert("Logs Flushed", "All queued client error and telemetry logs have been transmitted to the server.");
    } catch (err: any) {
      CustomAlert.alert("Error", err?.message || "Failed to flush log queue.");
    }
  };

  const handleClearTombstones = () => {
    if (tombstones.length === 0) return;

    CustomAlert.alert(
      "Clear Crash Tombstones",
      "Are you sure you want to delete all locally persisted crash tombstones?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Clear All",
          style: "destructive",
          onPress: async () => {
            try {
              await CrashlyticsService.clearRecentTombstones();
              await loadData();
              CustomAlert.alert("Tombstones Cleared", "Local crash tombstones have been cleared.");
            } catch (err: any) {
              CustomAlert.alert("Error", err?.message || "Failed to clear crash tombstones.");
            }
          },
        },
      ]
    );
  };

  const toggleExpandTombstone = (index: number) => {
    setExpandedTombstoneIndex((prev) => (prev === index ? null : index));
  };

  return (
    <ThemedScreen title="Crashlytics">
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <ThemedProgressBar loading={loading} />

        {/* Section 1: Service Configuration & Status */}
        <ThemedPanel
          title="Service Configuration"
          subtitle="Platform crash reporting integration and runtime diagnostics"
          variant="accent"
          accentColor="#E67E22"
          style={styles.panelSpacing}
        >
          <View style={styles.metadataGrid}>
            <View style={styles.metadataRow}>
              <ThemedText type="defaultSemiBold" style={styles.metaLabel}>
                Provider:
              </ThemedText>
              <ThemedText style={styles.metaValue}>
                {info.provider === "native_crashlytics"
                  ? "Native Firebase Crashlytics (Mobile)"
                  : "Google Cloud Logging Hybrid (Web SPA)"}
              </ThemedText>
            </View>

            <View style={styles.metadataRow}>
              <ThemedText type="defaultSemiBold" style={styles.metaLabel}>
                Platform OS:
              </ThemedText>
              <ThemedText style={styles.metaValue}>
                {info.platform.toUpperCase()}
              </ThemedText>
            </View>

            <View style={styles.metadataRow}>
              <ThemedText type="defaultSemiBold" style={styles.metaLabel}>
                Firebase Project:
              </ThemedText>
              <ThemedText style={styles.metaValue}>{info.projectId}</ThemedText>
            </View>

            <View style={styles.metadataRow}>
              <ThemedText type="defaultSemiBold" style={styles.metaLabel}>
                App / Product Version:
              </ThemedText>
              <ThemedText style={styles.metaValue}>
                v{info.appVersion} (Product: {info.productVersion})
              </ThemedText>
            </View>

            <View style={styles.metadataRow}>
              <ThemedText type="defaultSemiBold" style={styles.metaLabel}>
                Collection Status:
              </ThemedText>
              <View style={styles.statusBadgeRow}>
                <View
                  style={[
                    styles.statusBadge,
                    { backgroundColor: info.collectionEnabled ? "#27AE60" : "#7F8C8D" },
                  ]}
                >
                  <ThemedText style={styles.statusBadgeText}>
                    {info.collectionEnabled ? "ACTIVE" : "PAUSED"}
                  </ThemedText>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.actionRow}>
            <ThemedButton
              title="Open Firebase Console"
              icon={<Ionicons name="open-outline" size={16} color="#FFFFFF" />}
              onPress={handleOpenConsole}
              variant="secondary"
              style={styles.actionButton}
            />
            <ThemedButton
              title={info.collectionEnabled ? "Pause Collection" : "Resume Collection"}
              loading={toggling}
              onPress={handleToggleCollection}
              variant="secondary"
              style={styles.actionButton}
            />
          </View>
        </ThemedPanel>

        {/* Section 2: Diagnostics & Simulation Tools */}
        <ThemedPanel
          title="Diagnostic Tools"
          subtitle="Simulate exceptions to verify tombstone recording and remote flushing"
          variant="default"
          style={styles.panelSpacing}
        >
          <ThemedText style={[styles.descriptionText, { color: textMuted }]}>
            Use these controls to verify end-to-end exception tracking. When a test crash is triggered,
            the runtime captures stack telemetry, stores an on-device tombstone, and immediately dispatches
            the payload to Google Cloud Logging.
          </ThemedText>

          <View style={styles.actionRow}>
            <ThemedButton
              title="Trigger Test Crash"
              icon={<Ionicons name="flash-outline" size={16} color="#FFFFFF" />}
              loading={testingCrash}
              onPress={handleTriggerTestCrash}
              variant="danger"
              style={styles.actionButton}
            />
            <ThemedButton
              title="Flush Remote Logs"
              icon={<Ionicons name="cloud-upload-outline" size={16} color="#FFFFFF" />}
              onPress={handleFlushLogs}
              variant="secondary"
              style={styles.actionButton}
            />
          </View>
        </ThemedPanel>

        {/* Section 3: Recent Crash Tombstones */}
        <ThemedPanel
          title={`Persisted Crash Tombstones (${tombstones.length})`}
          subtitle="On-device crash records retained across application restarts"
          variant="default"
          headerRight={
            <View style={styles.headerRightActions}>
              <TouchableOpacity
                onPress={loadData}
                style={[styles.iconButton, { borderColor }]}
                accessibilityLabel="Refresh tombstones"
              >
                <Ionicons name="refresh" size={16} color={textMuted} />
              </TouchableOpacity>
              {tombstones.length > 0 && (
                <TouchableOpacity
                  onPress={handleClearTombstones}
                  style={[styles.iconButton, { borderColor }]}
                  accessibilityLabel="Clear tombstones"
                >
                  <Ionicons name="trash-outline" size={16} color="#E74C3C" />
                </TouchableOpacity>
              )}
            </View>
          }
          style={styles.panelSpacing}
        >
          {tombstones.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="checkmark-circle-outline" size={40} color="#27AE60" />
              <ThemedText type="defaultSemiBold" style={{ marginTop: 8 }}>
                No Crash Tombstones Found
              </ThemedText>
              <ThemedText style={{ color: textMuted, textAlign: "center", marginTop: 4 }}>
                The application runtime has not generated any fatal crash tombstones.
              </ThemedText>
            </View>
          ) : (
            tombstones.map((tombstone, index) => {
              const isExpanded = expandedTombstoneIndex === index;
              return (
                <View
                  key={`${tombstone.timestamp}-${index}`}
                  style={[styles.tombstoneCard, { backgroundColor: cardBg, borderColor }]}
                >
                  <TouchableOpacity
                    onPress={() => toggleExpandTombstone(index)}
                    style={styles.tombstoneHeader}
                  >
                    <View style={styles.tombstoneTitleRow}>
                      <View
                        style={[
                          styles.fatalBadge,
                          { backgroundColor: tombstone.isFatal ? "#E74C3C" : "#E67E22" },
                        ]}
                      >
                        <ThemedText style={styles.fatalBadgeText}>
                          {tombstone.isFatal ? "FATAL" : "HANDLED"}
                        </ThemedText>
                      </View>
                      <ThemedText type="stamp" style={{ color: textMuted, marginLeft: 8 }}>
                        {tombstone.timestampIso || new Date(tombstone.timestamp).toISOString()}
                      </ThemedText>
                    </View>
                    <Ionicons
                      name={isExpanded ? "chevron-up" : "chevron-down"}
                      size={18}
                      color={textMuted}
                    />
                  </TouchableOpacity>

                  <ThemedText type="defaultSemiBold" style={styles.tombstoneMessage}>
                    {tombstone.message}
                  </ThemedText>

                  {tombstone.context ? (
                    <ThemedText type="stamp" style={{ color: textMuted, marginTop: 4 }}>
                      Context: {tombstone.context}
                    </ThemedText>
                  ) : null}

                  {isExpanded && tombstone.stack ? (
                    <View style={styles.stackTraceContainer}>
                      <ThemedText type="code" style={styles.stackTraceText}>
                        {tombstone.stack}
                      </ThemedText>
                    </View>
                  ) : null}
                </View>
              );
            })
          )}
        </ThemedPanel>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    padding: Spacing.md || 16,
    paddingBottom: 40,
  },
  unauthorizedContainer: {
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  panelSpacing: {
    marginBottom: 16,
  },
  metadataGrid: {
    marginBottom: 16,
  },
  metadataRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(0,0,0,0.06)",
  },
  metaLabel: {
    fontSize: 14,
  },
  metaValue: {
    fontSize: 14,
  },
  statusBadgeRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  statusBadgeText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "bold",
  },
  actionRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  actionButton: {
    minHeight: 44,
  },
  descriptionText: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  headerRightActions: {
    flexDirection: "row",
    gap: 8,
  },
  iconButton: {
    width: 32,
    height: 32,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
  },
  tombstoneCard: {
    borderWidth: 1,
    borderRadius: 6,
    padding: 12,
    marginBottom: 10,
  },
  tombstoneHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  tombstoneTitleRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  fatalBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
  },
  fatalBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "bold",
  },
  tombstoneMessage: {
    fontSize: 14,
    marginTop: 4,
  },
  stackTraceContainer: {
    marginTop: 8,
    padding: 8,
    backgroundColor: "rgba(0,0,0,0.04)",
    borderRadius: 4,
  },
  stackTraceText: {
    fontSize: 11,
    lineHeight: 16,
  },
});
